'use client';
import { useEffect, useState } from 'react';
import WaSmartButton from './WaSmartButton';
import ContactButton from './ContactButton';
import AvatarZoom from './AvatarZoom';
import { avatarImg } from '@/lib/imgUrl';

type Props = {
  proArea?: import('@/lib/proCityGate').ProArea | null; // the message only names a city this pro serves (2026-09-28)
  // id of the element to watch — the shared header card (CategoryProCard) already
  // rendered on this page, with its own message/call buttons. Once it scrolls out of
  // view, these floating buttons take over as the always-reachable contact option.
  anchorId: string;
  proName: string;
  proFirstName?: string;
  proPhone?: string;
  whatsappValid?: number | null;
  proId?: number;
  proGender: string | null | undefined;
  categorySlug?: string;
  categoryName?: string | null;
  // 2026-09-16 (user request): on phones the reviews column has no sidebar, so once
  // the header card scrolls away nothing on screen says WHOSE reviews these are. When
  // set, the pro's photo floats as a third round element (same 52px size, same shadow)
  // on top of the message/call buttons — phones only (md:hidden); desktop gets the
  // sticky sidebar identity card (ProSidebarCard) instead.
  imgSrc?: string;
  // Founder 21/10/26: on the ct2 pro page the phone has no bottom dock — the pro's round photo + WA + call float bottom-left
  // instead. Phones (<768) only (.pfc-ct2m in app/globals.css); without it the ct2 chrome hides this component ([data-ct2-hide]).
  ct2Mobile?: boolean;
};

// 2026-08-23 (user request): on a professional's own page, scrolling past the header
// card's message/call buttons left no contact option reachable without scrolling back
// up. These two round floating buttons (bottom-left, above the persistent QuickConsult
// bar via --wa-bar-h) reuse the EXACT same WaSmartButton/ContactButton engine as the
// header card — same click handlers, same tracking, same live-updating WhatsApp message
// (WaSmartButton already re-reads the shared smartIntakeCtx store on every render, so
// this stays in sync with the questionnaire in real time with zero extra wiring here).
export default function ProFloatingContact({ anchorId, proName, proFirstName, proPhone, whatsappValid, proId, proGender, categorySlug, categoryName, imgSrc, proArea, ct2Mobile = false }: Props) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // anchorId lists "id" or "id:fraction" (2026-09-28, bughunter A2-A4): the floating buttons show while NO anchor is visible
    // enough INSIDE the usable viewport — the band above the fixed bottom bar (--wa-bar-h), clipped by any scrolling ancestor.
    // "pro-side-contact:0.9" = the desktop card's own WA/call row must be 90% visible; a bare id = any part of it.
    const specs = anchorId.split(',').map(x => { const [id, fr] = x.trim().split(':'); return { id, need: fr ? Number(fr) : 0.01 }; });
    const check = () => {
      const bar = ct2Mobile ? 0 : parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--wa-bar-h')) || 0; // ct2: no bottom bar
      const vis = specs.some(({ id, need }) => {
        const el = document.getElementById(id); if (!el || !el.offsetParent) return false; // display:none → not an anchor now
        const r = el.getBoundingClientRect(); if (!r.height) return false;
        let top = Math.max(r.top, 0), bottom = Math.min(r.bottom, window.innerHeight - bar);
        for (let p = el.parentElement; p; p = p.parentElement) { const o = getComputedStyle(p).overflowY; if (o === 'auto' || o === 'scroll' || o === 'hidden') { const pr = p.getBoundingClientRect(); top = Math.max(top, pr.top); bottom = Math.min(bottom, pr.bottom); } }
        return Math.max(0, bottom - top) / r.height >= need;
      });
      setShow(!vis);
    };
    check();
    window.addEventListener('scroll', check, { passive: true, capture: true }); window.addEventListener('resize', check);
    const t = setInterval(check, 1500); // the bottom bar changes height (--wa-bar-h) without a scroll
    return () => { window.removeEventListener('scroll', check, { capture: true } as any); window.removeEventListener('resize', check); clearInterval(t); };
  }, [anchorId, ct2Mobile]);

  if (!proPhone) return null;

  return (
    <div {...(ct2Mobile ? { className: 'pfc-ct2m' } : { 'data-ct2-hide': true })}
      style={{
        position: 'fixed', left: 14, bottom: ct2Mobile ? 'calc(env(safe-area-inset-bottom, 0px) + 14px)' : 'calc(var(--wa-bar-h, 0px) + 14px)', zIndex: 45,
        display: 'flex', flexDirection: 'column', gap: 10,
        opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(14px)',
        pointerEvents: show ? 'auto' : 'none',
        // visibility + inert: opacity/pointer-events alone left the invisible buttons in
        // the Tab order (Enter opened the zoom / fired WhatsApp from nothing visible —
        // bughunter 2026-09-16). visibility flips late on the way out so the fade completes.
        visibility: show ? 'visible' : 'hidden',
        transition: `opacity .25s ease, transform .25s ease, visibility 0s linear ${show ? '0s' : '.25s'}`,
      }}
      inert={!show}
    >
      {imgSrc && (
        <div className="md:hidden" data-floating-avatar>
          <AvatarZoom src={imgSrc} name={proFirstName || proName}>
            <img {...avatarImg(imgSrc, 52, { kind: 'pro', ctx: { category: categoryName, proName: (proFirstName || proName) } })} width={52} height={52} loading="lazy" decoding="async" referrerPolicy="no-referrer"
              className="rounded-full object-cover flex-shrink-0"
              style={{ width: 52, height: 52, border: '2.5px solid #fff', boxShadow: '0 4px 16px rgba(0,0,0,.25)', background: '#e0e7ff' }} />
          </AvatarZoom>
        </div>
      )}
      <WaSmartButton proArea={proArea ?? null} proName={proName} proFirstName={proFirstName} proPhone={proPhone} whatsappValid={whatsappValid}
        proId={proId} proGender={proGender} categorySlug={categorySlug} categoryName={categoryName} floating />
      <ContactButton proName={proName} proFirstName={proFirstName} proPhone={proPhone}
        proId={proId} proGender={proGender} categorySlug={categorySlug} categoryName={categoryName ?? undefined} floating />
    </div>
  );
}
